import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { Save, Trash2, Link, ExternalLink, Copy, Clock, Radio, CheckCircle, X } from 'lucide-react'
import { useAuth } from '../../../context/AuthContext'

export default function YTVideoDetail({ video, channels, allTags, tbtvChannels = [], cfLiveInputs = [], onUpdate, onDelete, onLink, onReselect, layout = 'split' }) {
  const { api } = useAuth()
  const navigate = useNavigate()
  const [saving, setSaving] = useState(false)
  const [status, setStatus] = useState('')
  const [unlinking, setUnlinking] = useState(false)

  // Editable fields
  const [eventType, setEventType] = useState('')
  const [durationMinutes, setDurationMinutes] = useState('')
  const [viloudChannelName, setViloudChannelName] = useState('')
  const [viloudChannelId, setViloudChannelId] = useState('')
  const [cfLiveInputId, setCfLiveInputId] = useState('')
  const [streamNotes, setStreamNotes] = useState('')
  const [includeInSchedule, setIncludeInSchedule] = useState(false)

  // Load from video prop
  useEffect(() => {
    if (!video) return
    setEventType(video.event_type || 'completed')
    setDurationMinutes(video.duration_minutes || '')
    setViloudChannelName(video.viloud_channel_name || '')
    setViloudChannelId(video.viloud_channel_id || '')
    setCfLiveInputId(video.cf_live_input_id || '')
    setStreamNotes(video.stream_notes || '')
    setIncludeInSchedule(!!video.include_in_schedule)
    setStatus('')
  }, [video])

  async function handleSave() {
    setSaving(true)
    try {
      await api(`/youtube/videos/${video.youtube_id}`, {
        method: 'PATCH',
        body: JSON.stringify({
          event_type: eventType,
          duration_minutes: durationMinutes ? parseInt(durationMinutes) : null,
          viloud_channel_name: viloudChannelName,
          viloud_channel_id: viloudChannelId,
          cf_live_input_id: cfLiveInputId,
          stream_notes: streamNotes,
          include_in_schedule: includeInSchedule,
        })
      })
      setStatus('✅ Saved')
      const updated = await api(`/youtube/videos/${video.youtube_id}`)
      onReselect(updated)
      onUpdate()
    } catch (e) {
      setStatus(`Error: ${e.message}`)
    }
    setSaving(false)
  }

  async function handleUnlink() {
    if (!confirm(`Clear VOD link from this YouTube video?\n\nThe VOD catalog entry will NOT be deleted.`)) return
    setUnlinking(true)
    try {
      await api(`/youtube/videos/${video.youtube_id}/unlink`, {
        method: 'POST'
      })
      setStatus('✅ VOD link cleared')
      const updated = await api(`/youtube/videos/${video.youtube_id}`)
      onReselect(updated)
      onUpdate()
    } catch (e) {
      setStatus(`Error: ${e.message}`)
    }
    setUnlinking(false)
  }

  function copyToClipboard(text) {
    navigator.clipboard.writeText(text)
    setStatus('✅ Copied')
  }

  function formatDate(dateStr) {
    if (!dateStr) return '—'
    return new Date(dateStr).toLocaleString()
  }

  if (!video) return null

  const metadataSection = (
    <div className="yt-detail-section">
      <h3>YouTube Metadata</h3>
      <div className="yt-meta-grid">
        <div className="yt-meta-item">
          <label>Channel</label>
          <div className="yt-meta-value">{video.channel_name || '—'}</div>
        </div>
        <div className="yt-meta-item">
          <label>Channel ID</label>
          <div className="yt-meta-value mono">
            {video.youtube_channel_id || '—'}
            {video.youtube_channel_id && (
              <button className="btn-ghost btn-sm" onClick={() => copyToClipboard(video.youtube_channel_id)}>
                <Copy size={11} />
              </button>
            )}
          </div>
        </div>
        <div className="yt-meta-item">
          <label>Scheduled</label>
          <div className="yt-meta-value">{formatDate(video.scheduled_start)}</div>
        </div>
        <div className="yt-meta-item">
          <label>Actual Start</label>
          <div className="yt-meta-value">{formatDate(video.actual_start)}</div>
        </div>
        {/* dev24: YouTube upload date (video.snippet.publishedAt). Distinct
            from Scheduled (live-only) and Synced (internal). This is what
            CatalogCreate uses to auto-fill release_date for VOD promotion. */}
        <div className="yt-meta-item">
          <label>Uploaded to YouTube</label>
          <div className="yt-meta-value">{formatDate(video.published_at)}</div>
        </div>
        <div className="yt-meta-item">
          <label>Viewers</label>
          <div className="yt-meta-value">{video.viewer_count || '—'}</div>
        </div>
        <div className="yt-meta-item">
          <label>Synced</label>
          <div className="yt-meta-value">{formatDate(video.synced_at)}</div>
        </div>
      </div>
      {video.description && (
        <div className="yt-description">
          <label>Description</label>
          <pre>{video.description}</pre>
        </div>
      )}
    </div>
  )

  const editSection = (
    <div className="yt-detail-section">
      <h3>TBTV Assignment</h3>

      <div className="yt-edit-grid">
        <div className="form-row">
          <label>Status</label>
          <select value={eventType} onChange={e => setEventType(e.target.value)}>
            <option value="upcoming">Upcoming</option>
            <option value="live">Live</option>
            <option value="completed">Completed</option>
          </select>
        </div>

        <div className="form-row">
          <label>Duration (min)</label>
          <input type="number" value={durationMinutes} onChange={e => setDurationMinutes(e.target.value)} />
        </div>

        <div className="form-row">
          <label>TBTV Channel</label>
          <select value={tbtvChannels.find(tc => tc.viloud_id === viloudChannelId)?.tbtv_channel_id || ''} onChange={e => {
            const ch = tbtvChannels.find(tc => tc.tbtv_channel_id === e.target.value)
            if (ch) { setViloudChannelName(ch.name || ''); setViloudChannelId(ch.viloud_id || ''); if (ch.cf_live_input_id) setCfLiveInputId(ch.cf_live_input_id) }
            else { setViloudChannelName(''); setViloudChannelId('') }
          }}>
            <option value="">— Select —</option>
            {tbtvChannels.map(tc => <option key={tc.viloud_id} value={tc.tbtv_channel_id}>{tc.name} [{tc.channel_type || 'linear'}]</option>)}
          </select>
        </div>

        <div className="form-row">
          <label>Viloud Channel ID</label>
          <input
            type="text"
            value={viloudChannelId}
            onChange={e => setViloudChannelId(e.target.value)}
            placeholder="Viloud ID"
          />
        </div>

        <div className="form-row">
          <label>CF Live Input</label>
          <select value={cfLiveInputId || ''} onChange={e => setCfLiveInputId(e.target.value)}>
            <option value="">— Select —</option>
            {cfLiveInputs.map(li => <option key={li.uid} value={li.uid}>{li.meta?.name || 'Unnamed'} ({li.uid.substring(0, 8)}…)</option>)}
          </select>
        </div>
      </div>

      <div className="form-row">
        <label>Stream Notes</label>
        <textarea
          value={streamNotes}
          onChange={e => setStreamNotes(e.target.value)}
          rows={2}
          placeholder="Notes..."
        />
      </div>

      <div className="form-row checkbox-row">
        <label>
          <input
            type="checkbox"
            checked={includeInSchedule}
            onChange={e => setIncludeInSchedule(e.target.checked)}
          />
          Include in Live Schedule
        </label>
      </div>

      <div className="yt-detail-save">
        <button className="btn-primary" onClick={handleSave} disabled={saving}>
          <Save size={14} /> {saving ? 'Saving...' : 'Save Changes'}
        </button>
      </div>
    </div>
  )

  return (
    <div className="yt-detail">
      {/* Header row */}
      <div className="yt-detail-header">
        <h2>{video.title || 'Untitled'}</h2>
        <div className="yt-detail-actions">
          {!video.tbtv_id && (
            <button className="btn-primary" onClick={() => navigate(`/ondemand/catalog?create=true&youtube_id=${video.youtube_id}`)}>
              <Link size={14} /> Create Content Entry
            </button>
          )}
          <button className="btn-danger" onClick={() => onDelete(video.youtube_id)}>
            <Trash2 size={14} />
          </button>
        </div>
      </div>

      {status && <div className={`yt-detail-status ${status.startsWith('Error') ? 'error' : ''}`}>{status}</div>}

      {/* Link Status Banner */}
      {video.tbtv_id && (
        <div className="yt-linked-banner">
          <Link size={14} />
          <span 
            className="yt-link-clickable"
            onClick={() => navigate(`/ondemand/catalog?select=${video.tbtv_id}`)}
            title="Go to VOD in Content Catalog"
          >
            In Content Library: <code>{video.tbtv_id}</code>
            <ExternalLink size={10} className="yt-link-icon" />
          </span>
          <button className="btn-ghost btn-sm" onClick={() => copyToClipboard(video.tbtv_id)}>
            <Copy size={12} />
          </button>
          <button
            className="btn-ghost btn-sm yt-unlink-btn"
            onClick={handleUnlink}
            disabled={unlinking}
            title="Clear VOD link (does not delete VOD)"
          >
            <X size={12} /> {unlinking ? 'Clearing...' : 'Clear Link'}
          </button>
        </div>
      )}

      {/* Main content */}
      {layout === 'split' ? (
        <div className="yt-detail-split">
          <div className="yt-detail-left">
            <div className="yt-embed-wrap">
              <iframe
                src={`https://www.youtube.com/embed/${video.youtube_id}`}
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
                title={video.title || 'YouTube Video'}
              />
            </div>
            <div className="yt-embed-actions">
              <a
                href={`https://www.youtube.com/watch?v=${video.youtube_id}`}
                target="_blank"
                rel="noopener noreferrer"
                className="yt-external-link"
              >
                <ExternalLink size={12} /> Watch on YouTube
              </a>
              <span className="yt-vid-id">
                {video.youtube_id}
                <button className="btn-ghost btn-sm" onClick={() => copyToClipboard(video.youtube_id)}>
                  <Copy size={11} />
                </button>
              </span>
            </div>
          </div>

          <div className="yt-detail-right">
            {metadataSection}
            {editSection}
          </div>
        </div>
      ) : (
        <div className="yt-detail-single">
          <div className="yt-embed-wrap">
            <iframe
              src={`https://www.youtube.com/embed/${video.youtube_id}`}
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
              allowFullScreen
              title={video.title || 'YouTube Video'}
            />
          </div>
          <div className="yt-embed-actions">
            <a
              href={`https://www.youtube.com/watch?v=${video.youtube_id}`}
              target="_blank"
              rel="noopener noreferrer"
              className="yt-external-link"
            >
              <ExternalLink size={12} /> Watch on YouTube
            </a>
            <span className="yt-vid-id">
              {video.youtube_id}
              <button className="btn-ghost btn-sm" onClick={() => copyToClipboard(video.youtube_id)}>
                <Copy size={11} />
              </button>
            </span>
          </div>
          {metadataSection}
          {editSection}
        </div>
      )}
    </div>
  )
}
